import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, PiggyBank, Sparkles } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/dates';
import { Badge, Button, Card, CardContent, EmptyState, ErrorState, Money } from '@/features/common/ui';
import type { Finding, SaveView } from './types';

/**
 * Save: where money is leaking, as findings to read, act on or dismiss -
 * subscriptions, price rises, bank charges, spending spikes, GST credit at
 * risk and TDS to tick off. Each shows what it is worth a year where that
 * can honestly be said, and the records behind it.
 */
export function SaveTab() {
  const org = useActiveOrg();
  const queryClient = useQueryClient();
  const [showHidden, setShowHidden] = useState(false);
  const view = useQuery({ queryKey: ['cfo-save', org?.id], queryFn: () => api.get<SaveView>('/cfo/save'), enabled: !!org?.id });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['cfo-save', org?.id] });
  };
  const dismiss = useMutation({
    mutationFn: (input: { finding: Finding; snooze: boolean }) => api.put('/cfo/dismissals', { cardId: `save:${input.finding.id}`, amount: input.finding.yearly ?? '0', snooze: input.snooze }),
    onSuccess: refresh,
  });
  const restore = useMutation({ mutationFn: (id: string) => api.delete(`/cfo/dismissals?cardId=${encodeURIComponent(`save:${id}`)}`), onSuccess: refresh });
  const claude = useMutation({ mutationFn: () => api.post('/cfo/save/claude'), onSuccess: refresh });

  if (view.isError) return <Card><ErrorState error={view.error} onRetry={() => view.refetch()} /></Card>;
  if (!view.data) return <div className="skeleton h-48 rounded-lg" />;
  const data = view.data;
  const shown = data.findings.filter((f) => !f.hidden);
  const hidden = data.findings.filter((f) => f.hidden);
  const waiting = data.ai?.status === 'queued' || data.ai?.status === 'claimed';
  const claudeError = claude.error instanceof ApiError ? claude.error.message : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          {shown.length ? (
            <>
              {shown.length} finding{shown.length === 1 ? '' : 's'}, worth about <Money value={data.yearly} currency={data.currency} className="font-semibold" /> a year where a figure can be put on it.
            </>
          ) : (
            'Nothing leaking that the books show.'
          )}
        </p>
        {shown.length ? (
          <Button size="sm" variant="outline" onClick={() => claude.mutate()} loading={claude.isPending} disabled={waiting}>
            <Sparkles className="size-4" aria-hidden /> {waiting ? 'Waiting for Claude' : 'Ask Claude to explain these'}
          </Button>
        ) : null}
      </div>
      {claudeError ? <p className="text-sm text-destructive">{claudeError}</p> : null}
      {data.ai?.status === 'failed' ? <p className="text-xs text-destructive">Claude could not explain them: {data.ai.error}</p> : null}

      {shown.length === 0 ? (
        <Card>
          <EmptyState icon={PiggyBank} title="Nothing found" description="Subscriptions, price rises, bank charges and GST credit at risk show here as the books find them." />
        </Card>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {shown.map((f) => (
            <FindingCard key={f.id} finding={f} currency={data.currency} onDismiss={(snooze) => dismiss.mutate({ finding: f, snooze })} />
          ))}
        </div>
      )}

      {hidden.length ? (
        <div className="space-y-2">
          <button type="button" className="inline-flex items-center gap-1 text-xs text-primary hover:underline touch:min-h-11" onClick={() => setShowHidden((v) => !v)}>
            {hidden.length} put away <ChevronDown className={cn('size-3 transition-transform', showHidden && 'rotate-180')} aria-hidden />
          </button>
          {showHidden ? (
            <ul className="space-y-1 text-sm">
              {hidden.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{f.title}</span>
                  <Button size="sm" variant="ghost" onClick={() => restore.mutate(f.id)}>
                    Bring back
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FindingCard({ finding, currency, onDismiss }: { finding: Finding; currency: string; onDismiss: (snooze: boolean) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Card data-save-finding={finding.kind}>
      <CardContent className="space-y-2 pt-4 text-sm">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold leading-snug">{finding.title}</h3>
          {finding.yearly ? (
            <Badge variant={finding.severity === 'medium' ? 'warning' : 'neutral'}>
              <Money value={finding.yearly} currency={currency} /> /yr
            </Badge>
          ) : null}
        </div>
        <p className="text-muted-foreground">{finding.detail}</p>
        {finding.note ? (
          <div className="space-y-1 rounded-md border border-border px-3 py-2">
            <p className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
              <Sparkles className="size-3" aria-hidden /> Claude
            </p>
            <p>{finding.note.explanation}</p>
            {finding.note.nextStep ? <p className="text-xs">Next: {finding.note.nextStep}</p> : null}
            {finding.note.falsePositive ? <p className="text-xs text-muted-foreground">Possibly not a problem: {finding.note.falsePositive}</p> : null}
          </div>
        ) : null}
        {finding.records.length ? (
          <>
            <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-xs text-primary hover:underline touch:min-h-11">
              The records ({finding.records.length}) <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} aria-hidden />
            </button>
            {open ? (
              <ul className="space-y-1 rounded-md bg-muted/50 px-3 py-2 text-xs">
                {finding.records.map((r) => (
                  <li key={r.id} className="flex justify-between gap-3">
                    <Link to={r.type === 'expense' ? '/expenses' : '/banking'} className="text-primary hover:underline">
                      {formatDate(r.date)} · {r.type === 'expense' ? 'expense' : 'bank line'}
                    </Link>
                    <Money value={r.amount} currency={currency} />
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={() => onDismiss(true)}>
            Snooze 2 weeks
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onDismiss(false)}>
            Dismiss
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
